// Common types used across the application

export interface User {
  id: string;
  name: string;
  email: string;
  avatar?: string;
}

export interface ApiResponse<T> {
  data: T;
  message?: string;
  success: boolean;
}

export class ApiError extends Error {
  public status: number;
  public code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

// Authentication types based on backend JWT implementation
export type Scope =
  | 'rental_agreements'
  | 'damage_waivers'
  | 'sequence'
  | 'check_in_forms'
  | 'guidebooks'
  | 'add_ons'
  | 'portal';

export interface AuthPayload {
  reservationId: string;
  guestId?: string;
  scope: Scope[];
  jti: string;
  exp: number;
  preview?: boolean;
  type?: string;
  templateId?: string;
  userId?: number;
}

export interface AuthState {
  isAuthenticated: boolean;
  token: string | null;
  payload: AuthPayload | null;
  // BES-7770: the direct-link code that minted the current token, so a direct
  // /:code/:page link can tell whether the cached session is already for it.
  authedCode: string | null;
  // BES-7770: the page key used to mint the token, so same-code-different-page
  // links re-validate instead of reusing a narrow-scoped token.
  authedPage: string | null;
  isLoading: boolean;
  error: string | null;
}

export interface AuthContextType extends AuthState {
  login: (token: string, code?: string, page?: string) => void;
  refreshToken: (token: string) => void;
  hasScope: (scope: Scope) => boolean;
  isTokenValid: () => boolean;
}

// Loyalty program types, hand-mirrored from
// backend/directBookingSite/loyaltyPrograms/types.ts.

export type LoyaltyMembershipStatus =
  | 'active'
  | 'expired'
  | 'canceled'
  | 'pending'
  | 'failed';

// Tiered loyalty config (BES-6632), hand-mirrored from
// backend/directBookingSite/loyaltyPrograms/config.ts. The clean-slate kind;
// the legacy paid_subscription_v1 shape was dropped.

export type LoyaltyPerkValue = { mode: 'percent' | 'fixed' | 'free'; amount?: number };

export type LoyaltyPerk =
  | { type: 'discount'; benefitKey: string; value: LoyaltyPerkValue; label?: string; conditions?: unknown[] }
  | { type: 'free_night'; thresholdNights: number; label?: string; conditions?: unknown[] }
  | { type: 'stored_credit'; benefitKey: string; amountCents: number; cycle: 'annual'; appliesTo: string[]; label?: string; conditions?: unknown[] };

export type LoyaltyPerkTarget =
  | { kind: 'booking_total' }
  | { kind: 'fee'; feeKey: string }
  | { kind: 'upsell'; upsellType: 'early_checkin' | 'late_checkout' }
  | { kind: 'custom_upsell'; upsellIds: number[] }
  | { kind: 'listing'; listingIds: number[] };

export interface LoyaltyTier {
  key: string;
  label: string;
  priceCents: number;
  currency: string;
  interval: 'month' | 'year';
  intervalCount?: number;
  stripePriceId?: string;
  perks: LoyaltyPerk[];
}

export interface TieredSubscriptionV1Config {
  kind: 'tiered_subscription_v1';
  tiers: LoyaltyTier[];
  targetCatalog: Record<string, LoyaltyPerkTarget[]>;
  platformFeePercent?: number;
  discountStacking?: 'stack' | 'single_best';
}

export type LoyaltyProgramConfig = TieredSubscriptionV1Config;

export interface LoyaltyProgram {
  id: number;
  tenantUserId: number;
  name: string;
  description: string | null;
  config: LoyaltyProgramConfig;
  enabled: boolean;
}

export interface LoyaltyMembershipWithStripe {
  id: number;
  programId: number;
  guestAccountId: number;
  status: LoyaltyMembershipStatus;
  stripeSubscriptionRecordId: number | null;
  metadata: Record<string, unknown>;
  stripe: {
    subscriptionRecordId: number;
    status: string;
    cancelAtPeriodEnd: boolean;
    currentPeriodStart: string | null;
    currentPeriodEnd: string | null;
    canceledAt: string | null;
  } | null;
}

// Compact membership summary the portal returns alongside the program. Used to
// gate the welcome modal / "Join" card when the guest has already signed up
// from this reservation. Full membership object lives behind a separate
// endpoint if it's needed later.
export interface LoyaltyEnrolledMembership {
  id: number;
  programId: number;
  status: LoyaltyMembershipStatus;
  /** The member's current tier, so the portal renders their perks (not tiers[0]). */
  currentTierKey: string | null;
}

// Pre-fill values for the signup form, sourced from the reservation we
// already know belongs to this guest. Each field is independently nullable:
// the frontend only collapses the corresponding input when the value is
// present, and falls back to a normal input otherwise.
export interface LoyaltyPrefill {
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
}

export interface LoyaltyProgramResponse {
  program: LoyaltyProgram | null;
  enrolledMembership: LoyaltyEnrolledMembership | null;
  prefill: LoyaltyPrefill | null;
}

export interface LoyaltyEmbeddedCheckoutSession {
  clientSecret: string;
  connectedAccountId: string;
  membershipId: number;
}

// Mirrors GET /loyalty/guest-balance from the guest-portal backend.
export interface GuestLoyaltyBalance {
  membershipId: number | null;
  programId: number | null;
  programName: string | null;
  currentTierKey: string | null;
  currency: string;
  storedCreditCents: number;
  creditPools: { benefitKey: string; availableCents: number; appliesTo: string[] }[];
  creditEligibleUpsellIds: number[];
  freeNightCount: number;
  freeNightThresholdNights: number | null;
  nightsUntilNextFreeNight: number | null;
}

export type LoyaltyErrorCode =
  | 'program_not_found'
  | 'program_disabled'
  | 'program_not_purchasable'
  | 'invalid_tier'
  | 'invalid_program_config'
  | 'membership_not_found'
  | 'already_active_membership'
  | 'already_pending_membership'
  | 'host_stripe_not_ready'
  | 'stripe_subscription_missing'
  | 'host_subdomain_not_configured'
  | 'tenant_mismatch'
  | 'email_taken'
  | 'invalid_input'
  | 'server_error';
